/**
 * Nearby Card Maps Module
 * Lazy-loads small Leaflet maps in tooltips when user clicks "View on map" links
 * Only initializes maps on demand to save API credits
 */

import { createOfficeIcon, createTileLayer } from './maps.js';

let activeTooltipMap = null;
let tooltipElement = null;
let mapContainer = null;
let activeLink = null;

export function initNearbyCardMaps() {
    const mapLinks = document.querySelectorAll('[data-map-link]');
    if (!mapLinks.length) return;

    mapLinks.forEach(link => {
        link.addEventListener('click', (e) => handleMapLinkClick(e, link));
    });

    document.addEventListener('click', (e) => {
        if (tooltipElement && !tooltipElement.contains(e.target) && !e.target.closest('[data-map-link]')) {
            hideTooltipMap();
        }
    });
}

function ensureTooltipCreated() {
    if (tooltipElement) return;

    tooltipElement = document.createElement('div');
    tooltipElement.className = 'tooltip-popover tooltip-popover--map';
    tooltipElement.setAttribute('data-position', 'left');

    const mapWrapper = document.createElement('div');
    mapWrapper.className = 'map-tooltip-wrapper';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'map-tooltip-close';
    closeBtn.setAttribute('aria-label', 'Close map');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        hideTooltipMap();
    });

    mapContainer = document.createElement('div');
    mapContainer.id = 'tooltip-map-container';

    const headerBand = document.createElement('div');
    headerBand.className = 'map-tooltip-header';
    headerBand.id = 'tooltip-map-header';

    mapWrapper.appendChild(closeBtn);
    mapWrapper.appendChild(mapContainer);
    mapWrapper.appendChild(headerBand);
    tooltipElement.appendChild(mapWrapper);
    document.body.appendChild(tooltipElement);
}

function handleMapLinkClick(e, link) {
    e.preventDefault();
    e.stopPropagation();

    ensureTooltipCreated();

    // Toggle off if clicking the same link
    if (activeLink === link && tooltipElement.classList.contains('tooltip-visible')) {
        hideTooltipMap();
        return;
    }

    const card = link.closest('.office-card--sidebar, .office-card--listing');
    if (!card) return;

    const lat = parseFloat(card.dataset.officeLat);
    const lng = parseFloat(card.dataset.officeLng);
    const name = card.dataset.officeName;

    if (isNaN(lat) || isNaN(lng)) return;

    if (!window.STADIA_KEY) {
        console.warn('Stadia API key not found');
        return;
    }

    // Position tooltip — at <880px use dialog mode (CSS handles it), otherwise position near link
    const isSmallScreen = window.innerWidth < 880;

    if (isSmallScreen) {
        tooltipElement.style.left = '';
        tooltipElement.style.top = '';
    } else {
        const rect = link.getBoundingClientRect();
        const tooltipWidth = 480;
        const tooltipHeight = 320;
        const gap = 14;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Determine best horizontal position: prefer left of link, fall back to right
        let left;
        let position;
        if (rect.left - tooltipWidth - gap >= 10) {
            // Fits to the left — arrow points right
            left = rect.left - tooltipWidth - gap;
            position = 'left';
        } else if (rect.right + gap + tooltipWidth <= vw - 10) {
            // Fits to the right — arrow points left
            left = rect.right + gap;
            position = 'right';
        } else {
            // Neither side fits cleanly — centre horizontally, no arrow
            left = Math.max(10, (vw - tooltipWidth) / 2);
            position = 'none';
        }

        // Vertical: centre on the link, clamp to viewport
        let top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        top = Math.max(10, Math.min(top, vh - tooltipHeight - 10));

        // Calculate arrow vertical offset (relative to tooltip)
        const arrowTop = rect.top + rect.height / 2 - top;

        tooltipElement.style.left = left + 'px';
        tooltipElement.style.top = top + 'px';
        tooltipElement.setAttribute('data-position', position);
        tooltipElement.style.setProperty('--tooltip-arrow-offset-block', arrowTop + 'px');
    }

    activeLink = link;
    tooltipElement.classList.add('tooltip-visible');

    // Disable card links while map is showing
    disableCardLinks();

    loadTooltipMap(lat, lng, name);
}

function hideTooltipMap() {
    if (tooltipElement) {
        tooltipElement.classList.remove('tooltip-visible');
    }
    if (activeTooltipMap) {
        activeTooltipMap.remove();
        activeTooltipMap = null;
    }
    activeLink = null;

    // Re-enable card links
    enableCardLinks();
}

function disableCardLinks() {
    const cards = document.querySelectorAll('.office-card--sidebar, .office-card--listing');
    cards.forEach(card => {
        card.classList.add('card-links-disabled');
        card.querySelectorAll('a').forEach(a => {
            a.dataset.originalHref = a.getAttribute('href');
            a.addEventListener('click', preventClick);
        });
    });
}

function enableCardLinks() {
    const cards = document.querySelectorAll('.card-links-disabled');
    cards.forEach(card => {
        card.classList.remove('card-links-disabled');
        card.querySelectorAll('a').forEach(a => {
            a.removeEventListener('click', preventClick);
        });
    });
}

function preventClick(e) {
    e.preventDefault();
    e.stopPropagation();
    hideTooltipMap();
}

function loadTooltipMap(lat, lng, name) {
    if (!mapContainer) return;

    // Remove old map if exists
    if (activeTooltipMap) {
        activeTooltipMap.remove();
    }

    // Reset loading state
    mapContainer.innerHTML = `
        <div class="map-loading">
            <div class="spinner"></div>
            <p>Loading map...</p>
        </div>
    `;

    activeTooltipMap = L.map(mapContainer, {
        scrollWheelZoom: false,
        attributionControl: false,
        dragging: true,
        touchZoom: false,
        zoomControl: false
    }).setView([lat, lng], 15);

    L.control.zoom({ position: 'bottomright' }).addTo(activeTooltipMap);

    createTileLayer().addTo(activeTooltipMap);

    L.marker([lat, lng], { icon: createOfficeIcon() }).addTo(activeTooltipMap);

    const headerBand = document.getElementById('tooltip-map-header');
    if (headerBand) {
        headerBand.textContent = name;
    }
}
